import { useEffect, useState } from "react";
import { creerLecteurContinu } from "../utils/continuationPlayback";

/**
 * Scène de la Centrifugeuse Laser 2.0 — REÇUE du backend, jamais redéfinie ici.
 *
 * Voir docs/DOCTRINE_GEOMETRIE_CANONIQUE.md et docs/PROCEDURE_CONFORMITE.md.
 *
 * ⚠️ Ce hook composait sa propre scène dans un `useMemo` — 16 sphères en double
 * couronne, 24 cubes en spirale, un tore — avec des couleurs tirées au
 * `Math.random()`. Le module portait alors QUATRE définitions concurrentes :
 * 10/8/16 sphères et 8/6/24 cubes selon la source, sans qu'aucune coïncide.
 *
 * Surtout, la seule vraie mécanique du module vivait dans
 * `backend/geometry/centrifuge_laser_v2/generator.py` — bras mobile, rebond,
 * collision, explosion — et elle était INATTEIGNABLE : le générateur était
 * réinstancié à chaque appel, donc aucun état ne survivait et la collision ne
 * pouvait jamais se produire.
 *
 * La forme canonique — arbitrage du 2026-07-29, option C avec particules —
 * garde la densité affichée (16 sphères, 24 cubes) et lui rend sa mécanique :
 * les sphères sont solidaires du bras 12h, les cubes du bras 6h mobile, qui
 * rebondit sur le nuage de sphères et percute le bras fixe.
 *
 * ⚠️ POPULATION VARIABLE. Les particules d'explosion apparaissent par
 * cinquantaines puis s'éteignent. Elles voyagent dans un champ SÉPARÉ de la
 * population persistante : leur indice ne désigne pas le même objet d'une
 * frame à l'autre.
 */
export default function useCentrifugeLaserV2Visualizer({
  speedPct = 50,
  ringSpeedPct = 70,
  turboPct = 0,
  steps = 300,
} = {}) {
  const [scene, setScene] = useState({
    objects: [],
    nSpheres: 0,
    core: null,
    arm12h: null,
    lecteur: null,
  });

  useEffect(() => {
    const controller = new AbortController();
    const json = (r) =>
      r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`));

    fetch("/api/geometry/centrifuge_laser_v2/initial", { signal: controller.signal })
      .then(json)
      .then((data) => {
        if (!Array.isArray(data.objects) || data.objects.length === 0) {
          throw new Error("réponse /initial invalide");
        }

        // Les curseurs paramètrent le MOTEUR (règle 2). `turbo` règle l'énergie
        // du billard, donc la fréquence des collisions : dans un billard
        // élastique l'énergie est une constante du mouvement, elle se fixe à la
        // création de la scène et non en cours de route.
        const params = new URLSearchParams({
          steps: String(steps),
          dt: "0.02",
          rotation: String(speedPct / 50),
          oscillation: String(ringSpeedPct / 70),
          turbo: String(0.5 + (turboPct / 100) * 1.5), // 0..100 % → 0,5..2,0
        });

        // ⚠️ LECTURE CONTINUE — le rejeu d'un segment unique téléportait la
        // scène à la couture (29x le pas typique). Les particules d'explosion
        // restent dans leur champ séparé : le lecteur ne les interprète pas,
        // il ne fait que livrer les frames (docs/SPEC_CONTINUATION.md § 6.2).
        const lecteur = creerLecteurContinu({
          url: "/api/geometry/centrifuge_laser_v2/animate",
          parametres: () => Object.fromEntries(params),
        });
        lecteur.demarrer();
        setScene({
          objects: data.objects,
          nSpheres: data.n_spheres,
          core: data.core,
          arm12h: data.arm_12h,
          lecteur,
        });
        return null;
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Erreur scène centrifuge_laser_v2:", err);
        }
      });

    return () => {
      controller.abort();
      setScene((precedent) => {
        if (precedent.lecteur) precedent.lecteur.arreter();
        return { ...precedent, lecteur: null };
      });
    };
  }, [speedPct, ringSpeedPct, turboPct, steps]);

  return scene;
}
